import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tab", "message", "platformInput", "disabledOverlay", "messageInput", "submitButton", "buttonIcon", "emailSubject", "standardInput", "emailInput", "richInput", "attachmentInput", "messageForm", "messengerLinkButton", "emailThreadToggle", "newThreadCheckbox", "threadHint", "subjectInput"]
  static values = { current: String, defaultPlatform: String, emailSubjectError: String, messengerBaseUrl: String, willReplyToThread: String, willStartNewThread: String }

  connect() {
    // Set default platform based on last received message or available contacts
    if (!this.currentValue && this.hasDefaultPlatformValue) {
      this.currentValue = this.defaultPlatformValue
    }
    this.showCurrentPlatform()

    // Initialize messenger link if available
    if (this.hasMessengerBaseUrlValue) {
      this.updateMessengerLink()
    }
  }

  switchTab(event) {
    const button = event.currentTarget
    const platform = button.dataset.platform
    
    // Update current value
    this.currentValue = platform
    
    // Update tab styles
    this.updateTabStyles(platform)
    
    // Show/hide messages based on platform
    this.showCurrentPlatform()
    
    // Update form platform
    this.updateFormPlatform(platform)
  }

  updateTabStyles(activePlatform) {
    this.tabTargets.forEach(tab => {
      const isActive = tab.dataset.platform === activePlatform
      
      if (isActive) {
        // Active style
        tab.classList.add('bg-white', 'shadow')
        tab.classList.remove('text-gray-600')
        
        if (activePlatform === 'instagram') {
          tab.classList.add('text-pink-600')
          tab.classList.remove('text-blue-600', 'text-green-600')
        } else if (activePlatform === 'email') {
          tab.classList.add('text-green-600')
          tab.classList.remove('text-pink-600', 'text-blue-600')
        } else {
          tab.classList.add('text-blue-600')
          tab.classList.remove('text-pink-600', 'text-green-600')
        }
      } else {
        // Inactive style
        tab.classList.remove('bg-white', 'shadow', 'text-pink-600', 'text-blue-600', 'text-green-600')
        tab.classList.add('text-gray-600')
      }
    })
    
    // Update submit button style based on platform
    this.updateSubmitButton(activePlatform)
  }
  
  updateSubmitButton(platform) {
    if (!this.hasSubmitButtonTarget) return
    
    const button = this.submitButtonTarget
    const icon = this.hasButtonIconTarget ? this.buttonIconTarget : button.querySelector('i')
    
    // Remove all platform-specific classes
    button.classList.remove('bg-pink-500', 'bg-blue-500', 'bg-green-500', 'hover:bg-pink-600', 'hover:bg-blue-600', 'hover:bg-green-600')
    
    // Apply platform-specific styles
    if (platform === 'instagram') {
      button.classList.add('bg-pink-500', 'hover:bg-pink-600')
      button.classList.remove('bg-primary', 'hover:bg-secondary')
      if (icon) {
        icon.className = 'fab fa-instagram'
      }
    } else if (platform === 'email') {
      button.classList.add('bg-green-500', 'hover:bg-green-600')
      button.classList.remove('bg-primary', 'hover:bg-secondary')
      if (icon) {
        icon.className = 'fas fa-envelope'
      }
    } else if (platform === 'messenger') {
      button.classList.add('bg-blue-500', 'hover:bg-blue-600')
      button.classList.remove('bg-primary', 'hover:bg-secondary')
      if (icon) {
        icon.className = 'fab fa-facebook-messenger'
      }
    }
  }

  showCurrentPlatform() {
    const currentPlatform = this.currentValue || this.defaultPlatformValue || 'instagram'
    
    // Don't filter messages anymore - show all messages chronologically
    // Just keep them all visible
    this.messageTargets.forEach(message => {
      message.style.display = ''
      message.classList.remove('hidden')
    })
    
    // Handle disabled overlay for Messenger
    if (this.hasDisabledOverlayTarget) {
      this.disabledOverlayTargets.forEach(overlay => {
        const overlayPlatform = overlay.dataset.platform
        if (overlayPlatform === currentPlatform) {
          overlay.classList.remove('hidden')
          // Disable text field and button
          if (this.hasMessageInputTarget) {
            this.messageInputTarget.disabled = true
          }
          if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = true
          }
        } else {
          overlay.classList.add('hidden')
          // Re-enable text field and button
          if (this.hasMessageInputTarget) {
            this.messageInputTarget.disabled = false
          }
          if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = false
          }
        }
      })
    }
    
    // Update form elements visibility
    this.updateFormElements(currentPlatform)
  }

  updateFormPlatform(platform) {
    // Update hidden form field for platform
    if (this.hasPlatformInputTarget) {
      this.platformInputTarget.value = platform
    }
    
    // Update form data-platform attribute
    if (this.hasMessageFormTarget) {
      this.messageFormTarget.dataset.platform = platform
    }
    
    // Show/hide form elements based on platform
    this.updateFormElements(platform)
  }
  
  updateFormElements(platform) {
    // Show/hide email subject field
    if (this.hasEmailSubjectTarget) {
      this.emailSubjectTargets.forEach(target => {
        const targetPlatforms = target.dataset.platform
        if (targetPlatforms && targetPlatforms.includes(platform)) {
          target.classList.remove('hidden')
        } else {
          target.classList.add('hidden')
        }
      })
    }
    
    // Show/hide standard input (Instagram/Messenger)
    if (this.hasStandardInputTarget) {
      this.standardInputTargets.forEach(target => {
        const targetPlatforms = target.dataset.platform
        if (targetPlatforms && targetPlatforms.includes(platform)) {
          target.classList.remove('hidden')
        } else {
          target.classList.add('hidden')
        }
      })
    }
    
    // Show/hide email input
    if (this.hasEmailInputTarget) {
      this.emailInputTargets.forEach(target => {
        const targetPlatforms = target.dataset.platform
        if (targetPlatforms && targetPlatforms.includes(platform)) {
          target.classList.remove('hidden')
        } else {
          target.classList.add('hidden')
        }
      })
    }
    
    // Show/hide attachment field
    if (this.hasAttachmentInputTarget) {
      this.attachmentInputTargets.forEach(target => {
        const targetPlatforms = target.dataset.platform
        if (targetPlatforms && targetPlatforms.includes(platform)) {
          target.classList.remove('hidden')
        } else {
          target.classList.add('hidden')
        }
      })
    }

    // Show/hide messenger link button vs regular submit button
    this.updateMessengerButton(platform)
  }

  updateMessengerButton(platform) {
    // Show m.me link button for messenger platform when available
    if (this.hasMessengerLinkButtonTarget && this.hasSubmitButtonTarget) {
      if (platform === 'messenger') {
        // Show messenger link button, hide submit
        this.messengerLinkButtonTarget.classList.remove('hidden')
        this.submitButtonTarget.classList.add('hidden')
      } else {
        // Show submit button, hide messenger link
        this.messengerLinkButtonTarget.classList.add('hidden')
        this.submitButtonTarget.classList.remove('hidden')
      }
    }
  }

  toggleEmailThread(event) {
    // Toggle between "reply to thread" and "new thread" mode
    const isNewThread = event.target.checked

    // Update the hint text
    if (this.hasThreadHintTarget) {
      const hintText = isNewThread
        ? `(${this.willStartNewThreadValue || 'will start a new thread'})`
        : `(${this.willReplyToThreadValue || 'will reply in the same thread'})`
      this.threadHintTarget.textContent = hintText
    }

    // Update the subject field
    if (this.hasSubjectInputTarget) {
      const subjectInput = this.subjectInputTarget
      const defaultReplySubject = subjectInput.dataset.defaultReplySubject || ''

      if (isNewThread) {
        // Clear subject for new thread (user can enter their own)
        subjectInput.value = ''
        subjectInput.placeholder = subjectInput.dataset.originalPlaceholder || 'Email subject...'
      } else {
        // Restore the reply subject
        subjectInput.value = defaultReplySubject
      }
    }
  }

  updateMessengerLink() {
    // Update the m.me link href with the message text
    if (!this.hasMessengerLinkButtonTarget || !this.hasMessageInputTarget) return

    const messageText = this.messageInputTarget.value.trim()
    const baseUrl = this.messengerBaseUrlValue

    if (baseUrl) {
      const url = messageText ? `${baseUrl}?text=${encodeURIComponent(messageText)}` : baseUrl
      this.messengerLinkButtonTarget.href = url
    }
  }

  // Helper method to determine available platforms based on contact info
  getAvailablePlatforms() {
    const availablePlatforms = []
    
    // Check which platforms are available based on contact information
    this.tabTargets.forEach(tab => {
      if (!tab.classList.contains('hidden')) {
        availablePlatforms.push(tab.dataset.platform)
      }
    })
    
    return availablePlatforms
  }
  
  // Template and quick actions methods
  async insertTemplate(event) {
    const templateType = event.currentTarget.dataset.templateType
    
    // Always switch to email platform for templates
    this.currentValue = 'email'
    this.updateTabStyles('email')
    this.updateFormPlatform('email')
    
    // Get booking ID from URL - handle both /admin/bookings/:id and other formats
    const pathMatch = window.location.pathname.match(/bookings\/(\d+)/)
    const bookingId = pathMatch ? pathMatch[1] : null
    
    if (!bookingId) {
      console.error('Could not determine booking ID from URL:', window.location.pathname)
      return
    }
    
    try {
      // Fetch templates from server with correct URL
      const url = `/admin/bookings/${bookingId}/fetch_email_templates`

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to fetch templates')
      }
      
      const data = await response.json()
      
      if (data.error) {
        console.error('Error fetching templates:', data.error)
        return
      }
      
      const template = data.templates[templateType]
      if (!template) {
        console.error('Template not found:', templateType)
        return
      }
      
      // Set email subject
      if (this.hasEmailSubjectTarget) {
        this.emailSubjectTargets.forEach(target => {
          const input = target.querySelector('input')
          if (input) {
            input.value = template.subject
          }
        })
      }
      
      // Set email content in Trix editor
      if (this.hasEmailInputTarget) {
        this.emailInputTargets.forEach(target => {
          const trixEditor = target.querySelector('trix-editor')
          if (trixEditor && trixEditor.editor) {
            // Load the HTML content directly from the template
            trixEditor.editor.loadHTML(template.body)
          }
        })
      }
    } catch (error) {
      console.error('Error inserting template:', error)
    }
  }

  async insertMessageTemplate(event) {
    const templateId = event.currentTarget.dataset.templateId
    const currentPlatform = this.currentValue || this.defaultPlatformValue || 'instagram'

    // Get booking ID from URL
    const pathMatch = window.location.pathname.match(/bookings\/(\d+)/)
    const bookingId = pathMatch ? pathMatch[1] : null

    if (!bookingId) {
      console.error('Could not determine booking ID from URL:', window.location.pathname)
      return
    }

    try {
      // Fetch processed template from server
      const url = `/admin/bookings/${bookingId}/fetch_message_template?template_id=${templateId}`

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch message template')
      }

      const data = await response.json()

      if (data.error) {
        console.error('Error fetching message template:', data.error)
        return
      }

      // Handle based on current platform
      if (currentPlatform === 'email') {
        // Email: insert title + content
        // Set email subject
        if (this.hasEmailSubjectTarget && data.title) {
          this.emailSubjectTargets.forEach(target => {
            const input = target.querySelector('input')
            if (input) {
              input.value = data.title
            }
          })
        }

        // Set email content in Trix editor
        if (this.hasEmailInputTarget && data.content) {
          this.emailInputTargets.forEach(target => {
            const trixEditor = target.querySelector('trix-editor')
            if (trixEditor && trixEditor.editor) {
              // Load the HTML content from the processed template
              trixEditor.editor.loadHTML(data.content)
            }
          })
        }
      } else {
        // Instagram/Messenger: insert only content in text field
        if (this.hasMessageInputTarget && data.content) {
          // Convert HTML to plain text while preserving line breaks
          // Replace HTML line breaks with actual newlines
          let html = data.content
          html = html.replace(/<br\s*\/?>/gi, '\n')  // Convert <br> to \n
          html = html.replace(/<\/div>/gi, '\n')     // Add newline after </div>
          html = html.replace(/<\/p>/gi, '\n\n')     // Add double newline after </p>

          // Create temp div with processed HTML
          const processedDiv = document.createElement('div')
          processedDiv.innerHTML = html
          const plainText = processedDiv.textContent || processedDiv.innerText || ''

          // Clean up extra newlines and trim
          const cleanText = plainText.replace(/\n\n\n+/g, '\n\n').trim()

          this.messageInputTarget.value = cleanText
          this.messageInputTarget.focus()
        }
      }

      // Close the dropdown
      if (event.currentTarget) {
        const dropdownMenu = event.currentTarget.closest('[data-dropdown-target="menu"]')
        if (dropdownMenu) {
          dropdownMenu.classList.add('hidden')
        }
      }
    } catch (error) {
      console.error('Error inserting message template:', error)
    }
  }

  insertVariable(event) {
    const variable = event.currentTarget.dataset.variable
    const platform = this.currentValue || 'instagram'
    
    if (platform === 'email' && this.hasEmailInputTarget) {
      // Insert into Trix editor
      const emailInputDiv = this.emailInputTargets.find(target => target.style.display !== 'none')
      if (emailInputDiv) {
        const trixEditor = emailInputDiv.querySelector('trix-editor')
        if (trixEditor && trixEditor.editor) {
          trixEditor.editor.insertString(variable)
        }
      }
    } else if (this.hasMessageInputTarget) {
      // Insert into text input at cursor position
      const input = this.messageInputTarget
      const startPos = input.selectionStart
      const endPos = input.selectionEnd
      const currentValue = input.value
      
      input.value = currentValue.substring(0, startPos) + 
                   variable + 
                   currentValue.substring(endPos)
      
      // Set cursor position after inserted variable
      const newPos = startPos + variable.length
      input.setSelectionRange(newPos, newPos)
      input.focus()
    }
  }
  
  clearForm(event) {
    event.preventDefault()

    // Clear all form inputs
    if (this.hasMessageInputTarget) {
      this.messageInputTarget.value = ''
    }

    if (this.hasEmailSubjectTarget) {
      this.emailSubjectTargets.forEach(target => {
        const input = target.querySelector('input')
        if (input) input.value = ''
      })
    }

    if (this.hasEmailInputTarget) {
      this.emailInputTargets.forEach(target => {
        const trixEditor = target.querySelector('trix-editor')
        if (trixEditor && trixEditor.editor) {
          trixEditor.editor.loadHTML('')
        }
      })
    }

    // Clear file inputs
    if (this.hasAttachmentsInputTarget) {
      this.attachmentsInputTarget.value = ''
    }
  }

  // Validate form before submission
  validateBeforeSubmit(event) {
    const currentPlatform = this.currentValue || this.defaultPlatformValue || 'instagram'

    // Only validate email subject for email platform
    if (currentPlatform === 'email') {
      let emailSubject = ''

      // Get email subject value
      if (this.hasEmailSubjectTarget) {
        this.emailSubjectTargets.forEach(target => {
          const input = target.querySelector('input')
          if (input && target.style.display !== 'none' && !target.classList.contains('hidden')) {
            emailSubject = input.value.trim()
          }
        })
      }

      // If email subject is empty, prevent submission and show error
      if (!emailSubject) {
        event.preventDefault()
        event.stopPropagation()

        // Show error message
        this.showEmailSubjectError()

        return false
      }
    }

    return true
  }

  showEmailSubjectError() {
    // Find the email subject field
    if (this.hasEmailSubjectTarget) {
      this.emailSubjectTargets.forEach(target => {
        const input = target.querySelector('input')
        if (input && target.style.display !== 'none' && !target.classList.contains('hidden')) {
          // Add red border
          input.classList.add('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
          input.classList.remove('border-gray-300')

          // Update placeholder
          const originalPlaceholder = input.placeholder
          input.placeholder = this.emailSubjectErrorValue

          // Focus the field
          input.focus()

          // Reset after 3 seconds
          setTimeout(() => {
            input.classList.remove('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
            input.classList.add('border-gray-300')
            input.placeholder = originalPlaceholder
          }, 3000)
        }
      })
    }
  }
}